import { Fragment, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel, Transition, TransitionChild, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { setSiteCurrency } from '@/utils/format';
import {
  HomeIcon,
  ShoppingBagIcon,
  CubeIcon,
  UsersIcon,
  ChartBarIcon,
  BellIcon,
  CogIcon,
  UserGroupIcon,
  CreditCardIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  MegaphoneIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  CodeBracketIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import type { NotificationPublic } from '@wooconnect/shared';
import { useUnreadCount, useRecentNotifications, useMarkRead } from '@/hooks/useNotifications';
import { SiteSelector } from '@/components/ui/SiteSelector';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/format';
import { translateLegacyNotification } from '@/utils/notificationTranslation';

function NotificationDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: unread } = useUnreadCount();
  const { data: notifications } = useRecentNotifications();
  const activeSiteId = useUIStore((s) => s.activeSiteId);
  const setActiveSite = useUIStore((s) => s.setActiveSite);
  const sites = useAuthStore((s) => s.sites);
  const markRead = useMarkRead();

  const iconMap: Record<string, typeof BellIcon> = {
    new_order: ShoppingBagIcon, order_status_changed: ShoppingBagIcon, refund_request: ShoppingBagIcon,
    low_stock: CubeIcon, product_backorder: CubeIcon,
    system: CogIcon, ai_insight: CogIcon, subscription_expiring: CogIcon, subscription_expired: CogIcon, plugin_required: CogIcon, bridge_offline: CogIcon,
  };

  const handleClick = (notif: NotificationPublic, close: () => void) => {
    if (!notif.isRead) markRead.mutate(notif.id);
    // Switch store if notification is for a different store
    if (notif.siteId && notif.siteId !== activeSiteId) {
      const matchedSite = sites.find((s) => s.id === notif.siteId);
      if (matchedSite) setActiveSite(matchedSite);
    }
    close();
    if (notif.resourceType === 'order' && notif.resourceId) navigate(`/orders/${notif.resourceId}`);
    else if (notif.resourceType === 'product' && notif.resourceId) navigate(`/products/${notif.resourceId}`);
    else navigate('/notifications');
  };

  return (
    <Popover className="relative">
      <PopoverButton className="relative rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none dark:text-gray-400 dark:hover:bg-gray-800">
        <BellIcon className="h-5 w-5" />
        {unread && unread.total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread.total > 99 ? '99+' : unread.total}
          </span>
        )}
      </PopoverButton>
      <PopoverPanel className="absolute right-0 z-50 mt-2 w-80 origin-top-right rounded-xl border border-gray-200 bg-white shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800">
        {({ close }) => (
          <>
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('notifications.title')}</p>
              {unread && unread.total > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600 dark:bg-red-900/30 dark:text-red-400">{unread.total}</span>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notifications && notifications.length > 0 ? (
                notifications.slice(0, 5).map((notif: NotificationPublic) => {
                  const Icon = iconMap[notif.type] ?? BellIcon;
                  const data = notif.data as Record<string, unknown> | undefined;
                  let displayTitle = data?.titleKey
                    ? t(data.titleKey as string, (data.titleParams ?? {}) as Record<string, string>)
                    : notif.title;
                  if (!data?.titleKey && displayTitle) {
                    displayTitle = translateLegacyNotification(displayTitle, '', t).title;
                  }
                  return (
                    <button key={notif.id} onClick={() => handleClick(notif, close)} className={cn('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50', !notif.isRead && 'bg-brand-50/30 dark:bg-brand-900/10')}>
                      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm', notif.isRead ? 'text-gray-600 dark:text-gray-400' : 'font-medium text-gray-900 dark:text-gray-100')}>{displayTitle}</p>
                        <p className="mt-0.5 text-xs text-gray-400">{formatRelativeTime(notif.createdAt)}</p>
                      </div>
                      {!notif.isRead && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand-500" />}
                    </button>
                  );
                })
              ) : (
                <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">{t('notifications.noNotifications')}</p>
              )}
            </div>
            <div className="border-t border-gray-100 px-4 py-2 dark:border-gray-700">
              <button onClick={() => { close(); navigate('/notifications'); }} className="w-full rounded-lg py-1.5 text-center text-sm font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20">
                {t('notifications.viewAll')}
              </button>
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}

interface NavItem {
  label: string;
  path: string;
  icon: typeof HomeIcon;
  badge?: number;
  external?: boolean;
}

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, sites, logout } = useAuthStore();
  const { sidebarOpen, setSidebarOpen, activeSiteId, setActiveSite, theme, setTheme, language, setLanguage } = useUIStore();
  const { data: unread } = useUnreadCount();

  // Auto-select first site if none selected, or hydrate activeSite from saved activeSiteId
  const activeSite = useUIStore((s) => s.activeSite);
  useEffect(() => {
    if (sites.length === 0) return;
    if (!activeSiteId) {
      setActiveSite(sites[0]!);
    } else if (!activeSite) {
      const match = sites.find((s) => s.id === activeSiteId);
      if (match) setActiveSite(match);
      else setActiveSite(sites[0]!);
    }
  }, [activeSiteId, activeSite, sites, setActiveSite]);

  // Sync formatCurrency with active site's currency code
  useEffect(() => {
    if (activeSite?.currency) setSiteCurrency(activeSite.currency);
  }, [activeSite?.currency]);

  const mainNav: NavItem[] = [
    { label: t('nav.dashboard'), path: '/dashboard', icon: HomeIcon },
    { label: t('nav.orders'), path: '/orders', icon: ShoppingBagIcon },
    { label: t('nav.products'), path: '/products', icon: CubeIcon },
    { label: t('nav.customers'), path: '/customers', icon: UsersIcon },
    { label: t('nav.analytics'), path: '/analytics', icon: ChartBarIcon },
  ];

  const notifNav: NavItem[] = [
    { label: t('nav.notifications'), path: '/notifications', icon: BellIcon, badge: unread?.total },
    { label: t('nav.settings'), path: '/settings', icon: CogIcon },
  ];

  const adminNav: NavItem[] = user?.role === 'super_admin' ? [
    { label: t('nav.users'), path: '/admin/users', icon: UserGroupIcon },
    { label: t('nav.subscriptions'), path: '/admin/subscriptions', icon: CreditCardIcon },
    { label: t('nav.plugins'), path: '/admin/plugins', icon: PuzzlePieceIcon },
    { label: t('nav.ai'), path: '/admin/ai', icon: SparklesIcon },
    { label: t('nav.marketing'), path: '/admin/marketing', icon: MegaphoneIcon },
    { label: t('nav.roleDefaults'), path: '/admin/role-defaults', icon: ShieldCheckIcon },
    { label: t('nav.auditLog'), path: '/admin/audit-log', icon: ClipboardDocumentListIcon },
    { label: t('nav.systemSettings'), path: '/admin/settings', icon: CogIcon },
    { label: t('nav.apiDocs'), path: 'https://api.wooconnectapp.com/docs', icon: CodeBracketIcon, external: true },
  ] : [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  function renderNav(items: NavItem[], sectionLabel?: string) {
    return (
      <div>
        {sectionLabel && (
          <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {sectionLabel}
          </p>
        )}
        <nav className="space-y-0.5">
          {items.map((item) =>
            item.external ? (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
              </a>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800',
                  )
                }
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            ),
          )}
        </nav>
      </div>
    );
  }

  const themeIcons = { light: SunIcon, dark: MoonIcon, system: ComputerDesktopIcon };
  const themeOptions: Array<{ value: 'light' | 'dark' | 'system'; Icon: typeof SunIcon }> = [
    { value: 'light', Icon: SunIcon },
    { value: 'dark', Icon: MoonIcon },
    { value: 'system', Icon: ComputerDesktopIcon },
  ];

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-14 items-center gap-2 px-4">
        <img src="/logo.svg" alt="WooConnect" className="h-8 w-8" />
        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">WooConnect</span>
      </div>

      {/* Site Selector */}
      <div className="px-3 pb-3">
        <SiteSelector
          sites={sites}
          activeSiteId={activeSiteId}
          onSelect={(site) => setActiveSite(site)}
        />
      </div>

      {/* Navigation */}
      <div className="flex-1 space-y-6 overflow-y-auto px-3">
        {renderNav(mainNav)}
        {renderNav(notifNav)}
        {adminNav.length > 0 && renderNav(adminNav, user?.role === 'super_admin' ? t('admin.section') : '')}
      </div>

      {/* Bottom section */}
      <div className="border-t border-gray-200 p-3 dark:border-gray-700">
        {/* Theme toggle */}
        <div className="mb-3 flex items-center justify-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          {themeOptions.map(({ value, Icon }) => (
            <button
              key={value}
              onClick={() => setTheme(value)}
              className={cn(
                'flex-1 rounded-md p-1.5 transition-colors',
                theme === value
                  ? 'bg-white shadow-sm dark:bg-gray-700'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
              )}
            >
              <Icon className="mx-auto h-4 w-4" />
            </button>
          ))}
        </div>

        {/* Language */}
        <div className="mb-3 flex items-center justify-center gap-1">
          {['en', 'el', 'de'].map((lang) => (
            <button
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                language === lang
                  ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
              )}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        {/* User */}
        <div className="flex items-center gap-3">
          <Avatar name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`} src={user?.avatarUrl} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {user?.firstName} {user?.lastName}
            </p>
            <Badge label={user?.role?.replace('_', ' ') ?? ''} size="sm" />
          </div>
          <button
            onClick={handleLogout}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title={t('auth.logout')}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 lg:block">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar */}
      <Transition show={sidebarOpen} as={Fragment}>
        <Dialog onClose={() => setSidebarOpen(false)} className="relative z-50 lg:hidden">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-900">
              <button
                onClick={() => setSidebarOpen(false)}
                className="absolute right-2 top-3 rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              {sidebarContent}
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-14 items-center gap-4 border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-900 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          {/* Notification dropdown */}
          <NotificationDropdown />

          {/* User avatar */}
          <Avatar name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`} src={user?.avatarUrl} size="sm" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
